import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaBriefcase, FaCheckCircle, FaLock } from 'react-icons/fa'
import { Button, TextArea } from '@components'
import { Spinner } from '@components/ui/Primitives'
import { usePermission, PermissionSet } from '@context'
import { fetchBusinessProfile, updateBusinessProfile, IBusinessProfile, IMcpSession } from '@query'

const MAX_WORDS = 250
// Hard character ceiling mirroring the backend's _MAX_CHARS, so a low-word/high-char paste
// (e.g. a long URL or token) can't pass the client and then be rejected server-side.
const MAX_CHARS = 10000

const countWords = (value: string): number => {
  const trimmed = value.trim()
  return trimmed ? trimmed.split(/\s+/).length : 0
}

const REL_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60]
]

// Localized "2 days ago" using the active i18n language, no extra dependency.
const formatRelative = (iso: string, lang: string): string => {
  const diffSec = Math.round((new Date(iso).getTime() - Date.now()) / 1000)
  const abs = Math.abs(diffSec)
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })
  if (abs < 60) return rtf.format(diffSec, 'second')
  for (const [unit, secs] of REL_UNITS) {
    if (abs >= secs) return rtf.format(Math.round(diffSec / secs), unit)
  }
  return rtf.format(Math.round(diffSec / 60), 'minute')
}

interface BusinessProfileSectionProps {
  session: IMcpSession
  ready: boolean
  slug?: string
}

// Business-owned, authoritative "what this business is" text that Luca reads as runtime context.
// Editing mirrors the main Business Information settings rights (admin-only, via
// PermissionSet.ADMIN); everyone else sees it read-only.
export const BusinessProfileSection: React.FC<BusinessProfileSectionProps> = ({
  session,
  ready,
  slug
}) => {
  const [t, i18n] = useTranslation()
  const queryClient = useQueryClient()
  const canEdit = usePermission(PermissionSet.ADMIN)
  const [draft, setDraft] = useState<string | null>(null)

  const profileKey = [slug, 'luca-business-profile']
  const {
    data: profile,
    isLoading,
    isError
  } = useQuery<IBusinessProfile>({
    queryKey: profileKey,
    queryFn: () => fetchBusinessProfile(session),
    enabled: ready
  })

  const saveMutation = useMutation({
    mutationFn: (description: string) => updateBusinessProfile(session, description),
    onSuccess: data => {
      queryClient.setQueryData(profileKey, data)
      setDraft(null)
      toast.success(t('lucaSettings.businessProfile.saved'))
    },
    onError: () => toast.error(t('lucaSettings.businessProfile.saveError'))
  })

  const saved = profile?.description ?? ''
  const value = draft ?? saved
  const wordCount = countWords(value)
  const overLimit = wordCount > MAX_WORDS
  const dirty = draft !== null && draft !== saved
  const updatedAt = profile?.updated_at
  const saving = saveMutation.isPending

  const renderEditor = () => (
    <>
      <TextArea
        id="luca-business-profile"
        value={value}
        onChange={e => setDraft(e.target.value)}
        placeholder={t('lucaSettings.businessProfile.placeholder')}
        rows={8}
        maxLength={MAX_CHARS}
        disabled={!ready || isLoading || saving}
        aria-label={t('lucaSettings.businessProfile.title')}
      />
      <FooterRow>
        <Meta>
          <WordCount $over={overLimit}>
            {t('lucaSettings.businessProfile.wordCount', { count: wordCount, max: MAX_WORDS })}
          </WordCount>
          {dirty ? (
            <Dot className="unsaved">{t('lucaSettings.businessProfile.unsaved')}</Dot>
          ) : saveMutation.isSuccess ? (
            <Dot className="saved">
              <FaCheckCircle size={13} /> {t('lucaSettings.businessProfile.savedTag')}
            </Dot>
          ) : (
            updatedAt && (
              <Muted>
                {t('lucaSettings.businessProfile.lastEdited', {
                  when: formatRelative(updatedAt, i18n.language)
                })}
              </Muted>
            )
          )}
        </Meta>
        <Button
          intent="primary"
          showSpinner={saving}
          onClick={() => saveMutation.mutate(value)}
          disabled={!ready || !dirty || overLimit || saving}
        >
          {t('lucaSettings.businessProfile.save')}
        </Button>
      </FooterRow>
    </>
  )

  return (
    <Card>
      <Header>
        <TitleRow>
          <IconBadge>
            <FaBriefcase />
          </IconBadge>
          <Title>{t('lucaSettings.businessProfile.title')}</Title>
          <VisibilityHint title={t('lucaSettings.businessProfile.visibleHint')}>
            <FaLock size={12} />
            <span>{t('lucaSettings.businessProfile.visibleHint')}</span>
          </VisibilityHint>
        </TitleRow>
        <Subtitle>{t('lucaSettings.businessProfile.description')}</Subtitle>
      </Header>

      {isLoading ? (
        // Hold the editor/read-only/empty decision until the fetch resolves, so a non-admin
        // doesn't flash "empty" and an admin doesn't briefly type into a stale empty box.
        <LoadingRow>
          <Spinner size={24} />
        </LoadingRow>
      ) : isError ? (
        // A failed load must not look like an empty profile, or an admin could type over the
        // real (temporarily unreadable) stored text and overwrite it on save.
        <ErrorMessage>{t('lucaSettings.businessProfile.loadError')}</ErrorMessage>
      ) : canEdit ? (
        renderEditor()
      ) : saved ? (
        <ReadOnlyText>{saved}</ReadOnlyText>
      ) : (
        <ReadOnlyEmpty>{t('lucaSettings.businessProfile.emptyReadOnly')}</ReadOnlyEmpty>
      )}
    </Card>
  )
}

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

// Icon, title, and the visibility hint share one row; the description spans full width beneath.
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const IconBadge = styled.div`
  flex: 0 0 auto;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.luca.deepBlue};
  background: ${({ theme }) => theme.luca.tint};
  font-size: 1rem;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
`

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.luca.navy};
`

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.55;
  margin: 0;
`

const VisibilityHint = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  white-space: nowrap;
`

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-wrap: wrap;
  min-width: 0;
`

const WordCount = styled.span<{ $over: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme, $over }) => ($over ? theme.colors.error : theme.colors.iconGray)};
`

const Dot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  &.unsaved {
    color: ${({ theme }) => theme.colors.warning};
  }
  &.saved {
    color: ${({ theme }) => theme.colors.success};
  }
`

const Muted = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl}rem 0;
`

const ReadOnlyText = styled.p`
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.5;
  margin: 0;
`

const ReadOnlyEmpty = styled.p`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-style: italic;
  margin: 0;
`

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  margin: 0;
`
